import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. `<Routes>` (classic React Router) has no
 * `errorElement`, so an uncaught render error anywhere in the app tree would
 * otherwise blank the whole window (white screen). This renders the error
 * instead — visible even in release builds (no devtools needed) — so failures
 * are diagnosable, with a reload escape hatch.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] uncaught render error', error, info);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          fontFamily: '-apple-system, system-ui, sans-serif',
          color: '#1e293b',
          background: '#f8fafc',
          textAlign: 'center',
        }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Something went wrong</h1>
        <p style={{ fontSize: 14, color: '#dc2626', margin: 0, maxWidth: 720 }}>{error.message}</p>
        {error.stack && (
          <pre
            style={{
              fontSize: 11,
              textAlign: 'left',
              maxWidth: 720,
              maxHeight: 240,
              overflow: 'auto',
              background: '#f1f5f9',
              padding: 12,
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
            }}>
            {error.stack}
          </pre>
        )}
        <button
          type='button'
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 14,
          }}>
          Reload
        </button>
      </div>
    );
  }
}
